import { Controller } from '@hotwired/stimulus';

export default class PatrimonyController extends Controller {
  static targets = [
    'incomePercentage',
    'incomeSum',
    'wealthPercentage',
    'wealthSum',
    'originsContainer',
    'originRow',
    'originTemplate',
    'originType',
    'originDescription',
    'originPercentage',
    'originSum',
    'addOriginButton',
  ];

  static values = {
    maxOrigins: { type: Number, default: 6 },
  };

  connect() {
    this.#updateIncomeSum();
    this.#updateWealthSum();
    this.#updateOriginSum();
    this.originTypeTargets.forEach(select => this.#syncDescription(select));
    this.#refreshOriginConstraints();
  }

  originTypeTargetConnected(select) {
    this.#initSelect2(select);
  }

  updateIncomeSum() {
    this.#updateIncomeSum();
  }

  updateWealthSum() {
    this.#updateWealthSum();
  }

  updateOriginSum() {
    this.#updateOriginSum();
  }

  addOrigin() {
    if (this.originRowTargets.length >= this.maxOriginsValue) return;

    const clone = this.originTemplateTarget.content.cloneNode(true);
    this.originsContainerTarget.appendChild(clone);
    this.#updateOriginSum();
    this.#refreshOriginConstraints();
    this.element.dispatchEvent(new Event('change', {bubbles: true}));
  }

  removeOrigin(event) {
    event.currentTarget.closest('[data-patrimony-target=\'originRow\']').remove();
    this.#updateOriginSum();
    this.#refreshOriginConstraints();
    this.element.dispatchEvent(new Event('change', {bubbles: true}));
  }

  toggleDescription(event) {
    this.#syncDescription(event.currentTarget);
    this.#refreshOriginConstraints();
  }

  clearIfNotPositive(event) {
    const input = event.currentTarget;
    if (input.value === '' || parseInt(input.value, 10) > 0) return;

    input.value = '';
    this.#updateOriginSum();
  }

  #updateIncomeSum() {
    const sum = this.#sumTargets(this.incomePercentageTargets);
    this.incomeSumTarget.textContent = `${sum} %`;
    this.#applyIndicatorStyle(this.incomeSumTarget, sum === 100);
  }

  #updateWealthSum() {
    const sum = this.#sumTargets(this.wealthPercentageTargets);
    this.wealthSumTarget.textContent = `${sum} %`;
    this.#applyIndicatorStyle(this.wealthSumTarget, sum === 100);
  }

  #updateOriginSum() {
    const sum = this.#sumTargets(this.originPercentageTargets);
    this.originSumTarget.textContent = `${sum} %`;
    this.#applyIndicatorStyle(this.originSumTarget, sum === 100);
  }

  #sumTargets(targets) {
    return targets.reduce((acc, el) => acc + (parseFloat(el.value) || 0), 0);
  }

  #applyIndicatorStyle(element, isValid) {
    element.classList.toggle('text-success', isValid);
    element.classList.toggle('text-danger', !isValid);
  }

  #syncDescription(select) {
    const row = select.closest('[data-patrimony-target=\'originRow\']');
    if (!row) return;

    const description = row.querySelector('[data-patrimony-target=\'originDescription\']');
    if (!description) return;

    const isOther = select.value === 'other';
    description.classList.toggle('d-none', !isOther);

    const input = description.querySelector('input, textarea');
    if (input) input.disabled = !isOther;
  }

  #initSelect2(select) {
    const $ = window.jQuery;
    if (!$ || typeof $.fn?.select2 !== 'function') return;

    const $el = $(select);
    if ($el.data('select2') !== undefined) return;

    const options = { minimumResultsForSearch: Infinity, width: '100%' };
    if ($el.find('option[value=""]').length) {
      options.placeholder = ' ';
    }
    $el.select2(options);
  }

  #refreshOriginConstraints() {
    if (this.hasAddOriginButtonTarget) {
      this.addOriginButtonTarget.disabled = this.originRowTargets.length >= this.maxOriginsValue;
    }

    const selectedValues = this.originTypeTargets
      .map(select => select.value)
      .filter(value => value !== '');

    this.originTypeTargets.forEach(select => {
      const currentValue = select.value;
      Array.from(select.options).forEach(option => {
        if (option.value === '' || option.value === currentValue) {
          option.disabled = false;
        } else {
          option.disabled = selectedValues.includes(option.value);
        }
      });
    });
  }
}
